import { VerifyOption } from '@/constant/VerifyConstant';
import { setTaskId } from '@/redux/slices/user';
import { init2FAVerify, verify2FAOption } from '@/services/user/security';
import { getDeviceId } from '@/utils/utils';
import { useEffect, useState } from 'react';

/**
 * 使用2FA 首先在业务开始前调用clearTaskRule();
 * 使用参考Security2FA, 各业务不用关心实现
 * 在需要2FA的页面注入 Security2FA（最好用AppModel弹出）传入callback（全部验证完成的回调）
 * 在callback中执行业务接口即可
 */
export default function use2FA({ callback }) {
  const [taskRuleParams, setTaskRuleParams] = useState(null);
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [taskRule, setTaskRule] = useState(null);
  const [currentVerify, setCurrentVerify] = useState(null);
  const [optionVerifyList, setOptionVerifyList] = useState([]);
  const [verifiedOptions, setVerifiedOptions] = useState([]);

  const [index, setIndex] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (taskRuleParams) {
      setAction(taskRuleParams.action);

      if (taskRuleParams.task) {
        setTaskRule(taskRuleParams.task);
      } else {
        _initTaskRule();
      }
    }
  }, [taskRuleParams]);

  useEffect(() => {
    if (taskRule) {
      setTaskId(taskRule.taskId);
      setTotal(taskRule.authCount ?? 0);
      _updateVerifyStatus();
    }
  }, [taskRule]);

  useEffect(() => {
    if (currentVerify) {
      _updateOptionVerifyList();
    }
  }, [currentVerify]);

  useEffect(() => {
    if (verifiedOptions?.length) {
      _updateVerifyStatus();
    }
  }, [verifiedOptions]);

  const _initTaskRule = async () => {
    try {
      setLoading(true);
      const res = await init2FAVerify({
        'action': taskRuleParams.action,
        'deviceId': getDeviceId(),
        'params': taskRuleParams.params,
        'expand': taskRuleParams.expand,
      });
      setTaskRule(res);
    } finally {
      setLoading(false);
    }
  }

  const _verifyOption = async () => {
    setVerifyLoading(true);
    try {
      await verify2FAOption();
    } finally {
      setVerifyLoading(false);
    }
  }

  const _updateVerifyStatus = async () => {
    if (!taskRule) return;

    let nextOption = _findNextOption(taskRule.optionsFix);
    if (!nextOption) {
      nextOption = _findNextOption(taskRule.options);
    }
    if (!nextOption) return;

    setCurrentVerify(nextOption);
  }

  const _updateOptionVerifyList = () => {
    if (!taskRule?.options?.length) return;

    setOptionVerifyList(taskRule.options.filter(opt => 
      verifiedOptions.indexOf(opt) == -1 && opt != currentVerify));
  }

  const _findNextOption = (options) => {
    if (!options?.length) return null;
    let nextOption = null;
    options.forEach(opt => {
      if (verifiedOptions.indexOf(opt) == -1 && nextOption == null) {
        nextOption = opt;
        return false;
      }
    });
    return nextOption;
  }

  const doVerifyOption = async (code) => {
    setVerifyLoading(true);
    try {
      let params = {
        taskId: taskRule.taskId,
      };
      if (currentVerify == VerifyOption.EMAIL) {
        params['emailCode'] = code;
      } else if (currentVerify == VerifyOption.MOBILE) {
        params['mobileCode'] = code;
      } else if (currentVerify == VerifyOption.GA) {
        params['googleCode'] = code;
      } else if (currentVerify == VerifyOption.PIN) {
        params['pinCode'] = code;
      }
      const res = await verify2FAOption(params);
      // all pass, 执行业务逻辑
      if (res.allPass) {
        _onAllPass();
        return;
      }
  
      // 进行下一项验证
      setIndex(index + 1);
      setVerifiedOptions(verifiedOptions.concat([currentVerify]));
    } finally {
      setVerifyLoading(false);
    }
  }

  const _onAllPass = async () => {
    callback?.()
  }

  return {
    action,
    total, index,

    setTaskRuleParams,
    taskRuleParams,
    taskRule,
    setCurrentVerify,
    doVerifyOption,
    loading,
    verifyLoading,
    currentVerify,
    optionVerifyList,
  };
}
